import React, { useContext, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled, { css, ThemeContext } from 'styled-components';

import { TagList, Tag, Button } from 'src/UIComponents';
import { useInfiniteSomethings } from 'src/queries/something';

const FlexContainer = styled.div`
  display: grid;
  grid-gap: 15px;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  grid-auto-flow: dense;
`;

const AnyCounter = styled.div`
  z-index: 0;
  position: absolute;
  bottom: -0.5em;
  right: -0.1em;
  font-size: 4em;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.N20};
`;

const CardLink = styled.div`
  position: relative;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.N0};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 5px solid ${({ theme }) => theme.colors.N20};
  border-image-slice: 1;
  border-image-source: linear-gradient(
    to bottom right,
    ${({ $colors }) => $colors.join(', ')},
    ${({ theme }) => theme.colors.N20}
  ); // https://css-tricks.com/gradient-borders-in-css/

  ${({ $colSpan }) =>
    $colSpan > 1 &&
    css`
      grid-column: span ${$colSpan};
    `}
  ${({ $rowSpan }) =>
    $rowSpan > 1 &&
    css`
      grid-row: span ${$rowSpan};
    `}

  transition: all 150ms ease-in-out;

  color: ${({ theme }) => theme.colors.N60};
  &:hover {
    cursor: pointer;
    transform: scale(1);
    background-color: ${({ theme }) => theme.colors.N20};
    border-color: ${({ theme }) => theme.colors.N30};
    color: ${({ theme }) => theme.colors.N70};
    ${AnyCounter} {
      color: ${({ theme }) => theme.colors.N10};
      transform: scale(1.45);
    }
  }
`;

const Title = styled.div`
  flex-grow: 1;
  z-index: 1;
  word-break: break-word;
  overflow: hidden;
  font-size: ${({ size }) => {
    if (size > 40) {
      return '1em';
    } else if (size > 30) {
      return '1.1em';
    } else if (size > 20) {
      return '1.2em';
    } else if (size > 15) {
      return '1.3em';
    } else if (size > 10) {
      return '1.6em';
    } else {
      return '2em';
    }
  }};
`;

export function SomethingItem({ value: item }) {
  const theme = useContext(ThemeContext);
  const navigate = useNavigate();
  const gotoSomething = useCallback(
    () => navigate(`/somethings/${item.id}`),
    [navigate, item.id]
  );
  const colors = useMemo(() => {
    return item?.Tags?.map((tag) => tag.color) ?? [theme.colors.primary];
  }, [item.Tags, theme]);
  const titleLength = item?.title?.length ?? 1;

  return (
    <CardLink
      onClick={gotoSomething}
      $colors={colors}
      $colSpan={item.Tags?.length > 2 ? 2 : 0}
      $rowSpan={titleLength > 27 ? 2 : 0}
    >
      <Title size={titleLength}>{item?.title}</Title>
      <AnyCounter>{item.Anythings?.length ?? ''}</AnyCounter>

      {item.Tags.length > 0 && (
        <TagList>
          {item.Tags.slice(0, 5).map((tag) => (
            <Link
              key={`tag-${tag.id}`}
              to={`/tags/${tag.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Tag color={tag.color} size="small">
                {tag.title}
              </Tag>
            </Link>
          ))}
        </TagList>
      )}
    </CardLink>
  );
}

function SomethingsList({ ...rest }) {
  const { data, fetchNextPage, isLoading, isFetching } =
    useInfiniteSomethings();

  if (isLoading) {
    return null;
  }

  const hasAnySomethings =
    data.pages && data.pages.length > 0 && data.pages[0].data?.length > 0;

  return (
    <>
      <FlexContainer {...rest}>
        {data.pages.map((page, pageIndex) => (
          <React.Fragment key={`page-${pageIndex}`}>
            {page.data.map((something) => (
              <SomethingItem
                key={`something-${something.id}`}
                value={something}
              />
            ))}
          </React.Fragment>
        ))}
      </FlexContainer>
      {hasAnySomethings && (
        <div
          style={{
            marginTop: '1em',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button disabled={isFetching} onClick={fetchNextPage}>
            {isFetching ? 'Loading...' : 'Load more'}
          </Button>
        </div>
      )}
    </>
  );
}

export default SomethingsList;
